/**
 * Evidence link (source_url) rules for post updates.
 * - No whitespace; max 2048 chars; hostname lowercased on normalize
 * - Only http:// or https://
 * - Hostname must include a domain + TLD (e.g. .com, .org, .io)
 * - Only URI-safe characters
 */

export const EVIDENCE_URL_MAX_LENGTH = 2048;

export type EvidenceUrlErrorKey =
  | 'POST_UPDATE_EVIDENCE_LINK_SPACES'
  | 'POST_UPDATE_EVIDENCE_LINK_MAX'
  | 'POST_UPDATE_EVIDENCE_LINK_PROTOCOL'
  | 'POST_UPDATE_EVIDENCE_LINK_DOMAIN'
  | 'POST_UPDATE_EVIDENCE_LINK_CHARS'
  | 'POST_UPDATE_EVIDENCE_LINK_INVALID';

/** URI characters allowed after normalize (RFC 3986 subset). */
const EVIDENCE_URL_SAFE_CHARS = /^[a-zA-Z0-9\-._~:/?#\[\]@!$&'()*+,;=%]*$/;

/**
 * Trim ends, lowercase scheme + hostname. Does not remove mid-string spaces
 * (those remain and fail validation).
 */
export function normalizeEvidenceUrl(raw: string | null | undefined): string {
  let url = String(raw ?? '').trim();
  if (!url) {
    return '';
  }
  const m = url.match(/^(https?):\/\/([^/?#]*)(.*)$/i);
  if (!m) {
    return url;
  }
  const scheme = m[1].toLowerCase();
  const host = m[2].toLowerCase();
  return `${scheme}://${host}${m[3]}`;
}

export function isValidEvidenceUrl(raw: string | null | undefined): boolean {
  return getEvidenceUrlError(raw) == null;
}

/** Empty link is allowed (evidence may be file-only). */
export function getEvidenceUrlError(
  raw: string | null | undefined
): EvidenceUrlErrorKey | null {
  if (raw == null || String(raw).trim().length === 0) {
    return null;
  }

  // Leading/trailing spaces are removed by normalize; mid-string spaces are invalid.
  const trimmed = String(raw).trim();
  if (/\s/.test(trimmed)) {
    return 'POST_UPDATE_EVIDENCE_LINK_SPACES';
  }

  const url = normalizeEvidenceUrl(trimmed);
  if (url.length > EVIDENCE_URL_MAX_LENGTH) {
    return 'POST_UPDATE_EVIDENCE_LINK_MAX';
  }

  if (!/^https?:\/\//i.test(url)) {
    return 'POST_UPDATE_EVIDENCE_LINK_PROTOCOL';
  }

  if (!EVIDENCE_URL_SAFE_CHARS.test(url)) {
    return 'POST_UPDATE_EVIDENCE_LINK_CHARS';
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'POST_UPDATE_EVIDENCE_LINK_INVALID';
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'POST_UPDATE_EVIDENCE_LINK_PROTOCOL';
  }

  const host = (parsed.hostname || '').toLowerCase();
  if (
    !host ||
    isIpOrLocalHost(host) ||
    !hasValidDomainAndTld(host) ||
    hasConsecutiveDuplicateLabels(host)
  ) {
    return 'POST_UPDATE_EVIDENCE_LINK_DOMAIN';
  }

  return null;
}

function isIpOrLocalHost(host: string): boolean {
  if (host === 'localhost') {
    return true;
  }
  // IPv4
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return true;
  }
  // IPv6 (URL hostname without brackets)
  if (host.includes(':')) {
    return true;
  }
  return false;
}

function isValidDnsLabel(label: string): boolean {
  if (!label || label.length > 63) {
    return false;
  }
  if (label.startsWith('-') || label.endsWith('-')) {
    return false;
  }
  return /^[a-z0-9-]+$/.test(label);
}

/** Requires at least domain.tld with alphabetic (or punycode) TLD. */
function hasValidDomainAndTld(host: string): boolean {
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 2) {
    return false;
  }
  if (!parts.every(isValidDnsLabel)) {
    return false;
  }
  const tld = parts[parts.length - 1];
  if (/^xn--[a-z0-9-]+$/.test(tld)) {
    return true;
  }
  // e.g. com, org, io, museum
  return /^[a-z]{2,63}$/.test(tld);
}

/** Reject hosts like example.com.com (repeated consecutive labels). */
function hasConsecutiveDuplicateLabels(host: string): boolean {
  const parts = host.split('.').filter(Boolean);
  for (let i = 1; i < parts.length; i++) {
    if (parts[i].toLowerCase() === parts[i - 1].toLowerCase()) {
      return true;
    }
  }
  return false;
}
